import { AccountInfo } from '@azure/msal-common';
import { Action, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ThunkAction } from 'redux-thunk';
import { UserBlModel, UserRolesBlModel } from '../../../Restful/LantisApi';
import { msalInstance } from '../../auth';
import { RootState } from '../../core/store';
import { apiCallBegan } from '../../core/store/api/action';
import CRUDRoles from '../../core/types/common/CRUDRoles';
export interface UserState {
    data: UserBlModel;
    roles: UserRolesBlModel[];
    dataError: string;
    dataLoading: boolean;
    rolesError: string;
    rolesLoading: boolean;
    azureAccount: AccountInfo | null;
}

const initialState: UserState = {
    data: {},
    roles: [],
    dataError: '',
    dataLoading: true,
    rolesError: '',
    rolesLoading: true,
    azureAccount: null,
};
const activeAccount = msalInstance.getActiveAccount();
const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        userRequested(state, _) {
            state.dataLoading = true;
            state.dataError = '';
        },
        userReceived(state, action: PayloadAction<UserBlModel>) {
            state.data = action.payload;
            state.dataLoading = false;
            state.azureAccount = activeAccount;
        },
        userRequestFailed(state, action: PayloadAction<string>) {
            state.dataLoading = false;
            state.dataError = action.payload;
        },
        rolesRequested(state, _) {
            state.rolesLoading = true;
            state.rolesError = '';
        },
        rolesReceived(state, action: PayloadAction<UserRolesBlModel[]>) {
            state.roles = action.payload;
            state.rolesLoading = false;
        },
        rolesRequestFailed(state, action: PayloadAction<string>) {
            state.rolesLoading = false;
            state.rolesError = action.payload;
        },
    },
});

export const userReducer = userSlice.reducer;
export const { userRequested, userReceived, userRequestFailed, rolesRequested, rolesReceived, rolesRequestFailed } =
    userSlice.actions;

export type UserActionTypes =
    | ReturnType<typeof userRequested>
    | ReturnType<typeof userReceived>
    | ReturnType<typeof userRequestFailed>
    | ReturnType<typeof rolesRequested>
    | ReturnType<typeof rolesReceived>
    | ReturnType<typeof rolesRequestFailed>;

// Selectors
export const contractsRolesSelector = createSelector(
    (state: RootState) => state.user.data,
    (state: RootState) => state.user.roles,
    (user, roles) => {
        const contractsRole = roles.find((role) => role.module?.moduleId === 19);
        const contractMethods = contractsRole?.module?.methods;

        const crudRoles: CRUDRoles = {
            view: contractsRole !== undefined || (user.typeEmploymentId === 1 && user.company === 'Lantis'),
            create: contractMethods?.some((method) => method.code === 'Create') || false,
            edit: contractMethods?.some((method) => method.code === 'Edit') || false,
            delete: contractMethods?.some((method) => method.code === 'Delete') || false,
        };

        return crudRoles;
    },
);

// Thunks
export const loadRoles = (): ThunkAction<void, RootState, unknown, Action<string>> => (dispatch, getState) => {
    return dispatch(
        apiCallBegan({
            url: '/api/Security/GetUserRightForModule',
            method: 'POST',
            safe: true,
            body: { id: activeAccount?.localAccountId },
            onStart: rolesRequested.type,
            onSuccess: rolesReceived.type,
            onError: rolesRequestFailed.type,
        }),
    );
};

export const loadUser = (): ThunkAction<void, RootState, unknown, Action<string>> => (dispatch, getState) => {
    return dispatch(
        apiCallBegan({
            url: '/api/User/GetUserBySharepointId',
            method: 'POST',
            safe: true,
            body: { id: activeAccount?.localAccountId },
            onStart: userRequested.type,
            onSuccess: userReceived.type,
            onError: userRequestFailed.type,
        }),
    );
};
